<template>
	<div
		v-if="renderDatepicker"
		ref="datepicker"
		:class="[
			skinClass,
			`datepicker__container--${position}`,
			{
				'datepicker__container--opened': inputCurrentlyOpened !== null,
				'datepicker__container--disabled': disabled,
				'datepicker__container--has-clear-button': hasClearButton,
				'datepicker__container--single': dynamicMonthsToShow === 1,
				'datepicker__container--inline': !isPopup,
				'datepicker__container--dates-blocked': blockedBefore,
				'datepicker__container--prev-button-blocked': blockedBefore,
			}
		]"
		class="datepicker__container datepicker-trigger"
		data-cy="datepicker-container"
	>
		<label
			v-if="label"
			class="datepicker__label"
		>
			{{ label }}

			<helio-information
				v-if="infoMessage"
				:color="infoColor"
				:icon="infoIcon"
				:message="infoMessage"
			/>
		</label>

		<div
			:class="inputsCompiledClasses"
			class="datepicker__inputs"
		>
			<helio-field
				ref="start"
				class="datepicker__input datepicker__input--start"
				:class="{
					'datepicker__input--disabled': disabled,
					'datepicker__input--opened': (inputCurrentlyOpened === 'start')
				}"
				data-cy="datepicker-input-start"
				:disabled="disabled"
				:display-success="false"
				:hint="hint || startHint"
				icon="calendar"
				icon-weight="light"
				:initial-errors="startErrors"
				:initial-value="formatDate(dateStart)"
				:large="large"
				:mask="translation.mask"
				:min-date="minDate"
				:pattern="translation.datePattern"
				:placeholder="translation.dateFormatGuide"
				tabindex="-1"
				@click.native="simulateClickOnTriggerer('start')"
				@focused="simulateClickOnTriggerer('start')"
				@changed="validateGivenDate('start', $event)"
				@blurred="blurredInline('start', $event)"
			/>

			<helio-field
				v-if="type === 'range'"
				ref="end"
				class="datepicker__input datepicker__input--end"
				:class="{
					'datepicker__input--disabled': disabled,
					'datepicker__input--opened': (inputCurrentlyOpened === 'end')
				}"
				data-cy="datepicker-input-end"
				:display-success="false"
				:large="large"
				:mask="translation.mask"
				:pattern="translation.datePattern"
				:placeholder="translation.dateFormatGuide"
				:disabled="disabled"
				:hint="hint || endHint"
				icon="calendar"
				icon-weight="light"
				:initial-errors="endErrors"
				:initial-value="formatDate(dateEnd)"
				tabindex="-1"
				@click.native="simulateClickOnTriggerer('end')"
				@focused="simulateClickOnTriggerer('end')"
				@changed="validateGivenDate('end', $event)"
				@blurred="blurredInline('end', $event)"
			/>

			<div
				:id="`datepicker-trigger-${_uid}`"
				:class="{
					'datepicker__triggerer--disabled': disabled
				}"
				class="datepicker__triggerer"
			/>
		</div>

		<AirbnbStyleDatepicker
			v-if="renderCalendar"
			ref="airbnb-datepicker"
			class="datepicker__popup"
			:date-one="dateStart"
			:date-two="dateEnd"
			:fullscreen-mobile="fullscreenMobileCalendar"
			:inline="!isPopup"
			:lang="$i18n.locale"
			:messages="messages"
			:min-date="blockedBefore"
			:mode="type"
			:months-to-show="dynamicMonthsToShow"
			:offset-y="offsetY"
			:trigger-element-id="`datepicker-trigger-${_uid}`"
			:show-shortcuts-menu-trigger="false"
			:trigger="isInline ? true : triggered"
			@date-one-selected="value => validateAndSetDateStart(value)"
			@date-two-selected="value => validateAndSetDateEnd(value)"
			@next-month="value => onFutureMonth(value)"
			@previous-month="value => onCurrentMonth(value)"
			@closed="closeDatepicker"
		>
			<template slot="close-icon">
				<helio-icon
					token="times"
					weight="light"
				/>
			</template>

			<template slot="previous-month-icon">
				<helio-icon
					:class="[
						'datepicker__previous-month-icon',
						{
							'datepicker__previous-month-icon--inactive': blockedBefore,
						}
					]"
					token="angle-left"
					weight="regular"
				/>
			</template>

			<template slot="next-month-icon">
				<helio-icon
					token="angle-right"
					weight="regular"
				/>
			</template>
		</AirbnbStyleDatepicker>
	</div>
</template>

<script>
import format from 'date-fns/format';
import moment from 'moment';
import en from '../../translations/en.json';
import fr from '../../translations/fr.json';
import Field from '../field/Field.vue';
import Icon from '../icon/Icon.vue';
import Information from '../information/Information.vue';

export default {
	name: 'Datepicker',

	components: {
		'helio-field': Field,
		'helio-icon': Icon,
		'helio-information': Information,
	},

	props: {
		clearable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the clear button will appear, allowing users to clear their selections',
		},

		customInlineBreakpoint: {
			type: Number,
			required: false,
			default: null,
			description: 'Sets a custom breakpoint above which a Datepicker will be in inline mode',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Datepicker will be disabled for the user',
		},

		fullscreenMobile: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Datepicker displays a fullscreen calendar at mobile widths',
		},

		hint: {
			type: String,
			required: false,
			default: null,
			description: 'Sets custom datepicker hint',
		},

		infoColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the color of the infoIcon',
		},

		infoIcon: {
			type: String,
			required: false,
			default: 'question-circle',
			description: 'Sets the icon that will appear next to the Datepicker\'s label. Clicking the icon will open a tooltip.',
		},

		infoMessage: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the message that will appear in the icon\'s tooltip',
		},

		initialDateEnd: {
			type: String,
			required: false,
			default: '',
			description: 'Sets whether the input will be populated with a specific end date',
		},

		initialDateStart: {
			type: String,
			required: false,
			default: '',
			description: 'Sets whether the input will be populated with a specific start date',
		},

		inputWidth: {
			type: String,
			required: false,
			default: 'boxed',
			validator: value => [
				'boxed',
				'grow',
			].indexOf(value) !== -1,
			description: 'Sets whether the width of the input box will be limited (boxed) or if it will grow to the width of the container',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text of the Datepicker\'s label',
		},

		large: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the height of the input field will be large or regular',
		},

		layout: {
			type: String,
			required: false,
			default: 'popup',
			validator: value => [
				'inline',
				'popup',
			].indexOf(value) !== -1,
			description: 'Sets whether the datepicker will be shown inline of the date field (inline) or will appear only when triggered (popup). Inline layout will be applied only at container widths wide enough to permit it.',
		},

		monthsToShow: {
			type: Number,
			required: false,
			default: null,
			validator: monthsToShow => monthsToShow >= 0,
			description: 'Sets whether 1 or 2 months will be shown in the calendar view of the Datepicker',
		},

		pastDatesBlocked: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether user can select dates in the past',
		},

		position: {
			type: String,
			required: false,
			default: 'bottom',
			validator: value => [
				'bottom',
				'left',
				'right',
				'top',
			].indexOf(value) !== -1,
			description: 'Sets where the calendar view will appear in relation to the date input fields.',
		},

		skin: {
			type: String,
			required: false,
			default: null,
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets whether the Datepicker will have a light or dark skin',
		},

		type: {
			type: String,
			required: false,
			default: 'range',
			validator: value => [
				'range',
				'single',
			].indexOf(value) !== -1,
			description: 'Sets the type of calendar view that will be shown',
		},
	},

	data() {
		return {
			currentLocale: '',
			dateStart: '',
			dateEnd: '',
			messages: {
				en: null,
				fr: null,
			},
			endHint: null,
			endErrors: [],
			startHint: null,
			startErrors: [],
			triggered: false,
			inputCurrentlyOpened: null,
			popupHeight: 380,
			renderDatepicker: false,
			renderCalendar: true,
			dateRawFormat: 'YYYY-MM-DD',
			skinClass: '',
			hasClearButton: false,
			dynamicLayout: 'popup',
			fullscreenMobileCalendar: null,
			widthMode: null,
		};
	},

	computed: {
		blockedBefore() {
			if (this.pastDatesBlocked) {
				const date = new Date();
				date.setDate(date.getDate() - 1);

				return date.toGMTString();
			}

			return '';
		},

		minDate() {
			if (this.blockedBefore) {
				const date = new Date();

				// We need the ISO format to be able to compare it
				// with the datepicker's field date validator (YYYY-mm-dd in regex)
				return date.toISOString().split('T')[0];
			}
			return null;
		},

		currentMonth() {
			const date = new Date();
			return date.getMonth() + 1;
		},

		dynamicMonthsToShow() {
			if (this.monthsToShow) {
				return this.monthsToShow;
			}

			return (this.type === 'range' && this.dynamicLayout === 'inline') ? 2 : 1;
		},

		inputsCompiledClasses() {
			let compiledClasses = `datepicker__inputs--${this.inputWidth} `;

			if (this.type === 'range') {
				compiledClasses += 'datepicker__inputs--multiple ';
			}

			if (!this.isPopup) {
				compiledClasses += 'datepicker__inputs--inline ';
			}

			return compiledClasses;
		},

		isPopup() {
			return this.dynamicLayout === 'popup';
		},

		isInline() {
			return this.dynamicLayout === 'inline';
		},

		offsetY() {
			// If this is a popup top, we sum the popup height and a little gap
			if (this.isPopup && this.position === 'top' && this.popupHeight !== 0) {
				const gap = this.label ? 18 : 10;
				return (this.popupHeight + gap) * -1;
			}

			// Otherwise..
			return this.label ? 86 : 70;
		},

		translation() {
			return this.messages[this.$i18n.locale];
		},

		english() {
			return { ...en };
		},

		french() {
			return { ...fr };
		},
	},

	watch: {
		$props: {
			immediate: true,
			handler() {
				// We need this to validate a props based on another props
				this.validateProps();
			},
		},

		disabled() {
			this.setCalendarButtons();
			this.deactivatePreviousMonthButton();
		},

		initialDateEnd(newDate) {
			this.validateAndSetDateEnd(newDate);
		},

		initialDateStart(newDate) {
			this.validateAndSetDateStart(newDate);
		},

		renderCalendar() {
			if (!this.renderCalendar) {
				this.$nextTick(() => {
					this.renderCalendar = true;
				});
			}
		},

		triggered() {
			if (this.isInline) {
				return;
			}

			if (this.triggered) {
				this.$emit('opened');
				this.addClearButton();
				this.calculateOffset();
				this.openDatepicker();
			} else {
				this.$emit('closed');
				this.closeDatepicker();
			}
		},
	},

	mounted() {
		this.currentLocale = this.$i18n.locale;
		this.validateAndSetDateStart(this.initialDateStart);
		this.validateAndSetDateEnd(this.initialDateEnd);
		this.endHint = this.translation.dateFormatGuide;
		this.startHint = this.translation.dateFormatGuide;

		this.onResize();
		window.addEventListener('resize', this.onResize);
		setTimeout(() => {
			this.onResize();
		}, 1000);

		setTimeout(() => {
			this.renderDatepicker = true;
		}, 300);

		setTimeout(() => {
			const { datepicker } = this.$refs;

			if (!datepicker) {
				return;
			}

			// Which skin is the closest outside of the component
			const closestSkin = datepicker.closest('.dark-theme') ? 'dark' : 'light';

			// If there's a skin given in props we do use it, otherwise we take the closest
			const skinToAssign = this.skin ? this.skin : closestSkin;

			// Change the skin class based on the skin we've found
			this.skinClass = `datepicker__container--${skinToAssign}`;
		}, 500);

		setTimeout(() => {
			// deactivate/activate calendar per prop disabled
			this.setCalendarButtons();
			this.deactivatePreviousMonthButton();
		}, 500);
	},

	updated() {
		this.$nextTick(() => {
			if (this.currentLocale !== this.$i18n.locale) {
				this.updateTranslations();
			}
		});
	},

	created() {
		const ariaLabelsFr = this.generateAriaLabels(this.french);
		const ariaLabelsEn = this.generateAriaLabels(this.english);
		this.messages.en = { ...this.english.datepicker.visibleText, ...ariaLabelsEn };
		this.messages.fr = { ...this.french.datepicker.visibleText, ...ariaLabelsFr };
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		onResize() {
			this.$nextTick(() => {
				const width = window.innerWidth;

				if (this.customInlineBreakpoint && width >= this.customInlineBreakpoint) {
					this.widthMode = 'inlineWidth';
				} else if (this.type === 'range' && width >= 960) {
					this.widthMode = 'inlineWidth';
				} else if (this.type === 'single' && width >= 600) {
					this.widthMode = 'inlineWidth';
				} else if (width >= 400) {
					this.widthMode = 'popupWidth';
				} else {
					this.widthMode = 'fullscreenWidth';
				}

				this.setOptions();
			});
		},

		setOptions() {
			const fullscreenCalendarWidth = this.widthMode === 'fullscreenWidth';
			const popupWidth = this.widthMode !== 'inlineWidth';
			const inlineWidth = this.widthMode === 'inlineWidth';

			/* if less than inlineWidth, use popup layout with bottom position,
			otherwise use layout and position as passed through props */
			this.dynamicLayout = popupWidth
				? 'popup'
				: this.layout;
			this.dynamicPosition = popupWidth
				? 'bottom'
				: this.position;

			/* if fullscreenMobile prop OR fullscreenCalendarWidth is true,
			use fullscreen version of calendar */
			this.fullscreenMobileCalendar = this.fullscreenMobile
				? this.fullscreenMobile
				: fullscreenCalendarWidth;

			/* if dynamicLayout is inline and inlineWidth is true, trigger calendar */
			this.triggered = (this.dynamicLayout === 'inline') && inlineWidth;
		},

		generateAriaLabels(translations) {
			return {
				ariaLabels: {
					chooseDate: (date) => translations.datepicker.ariaLabels.chooseDate.replace('[[date]]', date),
					chooseStartDate: (date) => translations.datepicker.ariaLabels.chooseStartDate.replace('[[date]]', date),
					chooseEndDate: (date) => translations.datepicker.ariaLabels.chooseEndDate.replace('[[date]]', date),
					selectedDate: (date) => translations.datepicker.ariaLabels.selectedDate.replace('[[date]]', date),
					unavailableDate: (date) => translations.datepicker.ariaLabels.unavailableDate.replace('[[date]]', date),
					previousMonth: translations.datepicker.previousMonth,
					nextMonth: translations.datepicker.nextMonth,
					closeDatepicker: translations.datepicker.closeDatepicker,
					openKeyboardShortcutsMenu: translations.datepicker.openKeyboardShortcutsMenu,
					closeKeyboardShortcutsMenu: translations.datepicker.closeKeyboardShortcutsMenu,
				},
			};
		},

		validateAndSetDateStart(newDate = '') {
			// eslint-disable-next-line
			const formattedDate = newDate ? newDate : '';
			const date = this.universalToCurrentLocale(formattedDate);
			this.validateGivenDate('start', date);
			this.dateStart = formattedDate;
		},

		validateAndSetDateEnd(newDate = '') {
			// eslint-disable-next-line
			const formattedDate = newDate ? newDate : '';
			const date = this.universalToCurrentLocale(formattedDate);
			this.validateGivenDate('end', date);
			this.dateEnd = formattedDate;
		},

		universalToCurrentLocale(date) {
			return this.$i18n.locale === 'fr'
				? this.universalDateToFrench(date)
				: this.universalDateToEnglish(date);
		},

		validateProps() {
			if (this.layout === 'popup' && (this.position === 'left' || this.position === 'right')) {
				console.error('Invalid prop: type check failed for prop "position". Expected String with value being "top" or "bottom".');
			}
		},

		updateTranslations() {
			if (this.minDate) {
				if (this.startErrors.length > 0) {
					this.startErrors = [this.messages[this.$i18n.locale].startDateError];
				}
			}

			if (this.endErrors.length > 0 && !this.dateStart) {
				this.endErrors = [this.messages[this.$i18n.locale].startDateMissing];
			} else if (this.endErrors.length > 0) {
				this.endErrors = [this.messages[this.$i18n.locale].endDateTooEarly];
			}

			if (this.triggered) {
				this.addClearButton();
			}

			this.currentLocale = this.$i18n.locale;
		},

		openDatepicker() {
			setTimeout(() => {
				this.renderCalendar = true;
				this.triggered = true;
				// deactivate/activate calendar per prop disabled
				this.setCalendarButtons();
				this.deactivatePreviousMonthButton();
			}, 100);
		},

		simulateClickOnTriggerer(input) {
			if (this.inputCurrentlyOpened === input || this.disabled) {
				return;
			}

			// We're forced to cascade the events in order for
			// VueAirBnbDatepicker to let us dynamically open
			// the datepicker
			this.$parent.$emit('datePickerToggle', true);

			setTimeout(() => {
				this.triggered = false;
				this.openDatepicker();
			}, 50);
		},

		// ] Methods that manage collection/validation of date

		formatDate(date) {
			if (date && this.translation.dateFormat) {
				return format(date, this.translation.dateFormat);
			}

			return '';
		},

		validateGivenDate(input, date) {
			const currentDateStart = this.formatDate(this.dateStart);
			const currentDateEnd = this.formatDate(this.dateEnd);

			// If the given date is the same as in the component data
			// it means the changes came from the Datepicker library
			// and we don't want to do anything
			if (
				(input === 'start' && date === currentDateStart)
				|| (input === 'end' && date === currentDateEnd)
			) {
				return;
			}

			if (date === '') {
				if (input === 'start') {
					this.updateStartDate(date);
				} else if (input === 'end') {
					this.updateEndDate(date);
				}
			}

			// We want to make sure it fits our dateFormat format
			const dateRegEx = this.$i18n.locale === 'fr' ? /^\d{2}\/\d{2}\/\d{4}$/ : /^\d{4}\/\d{2}\/\d{2}$/;

			if (date.match(dateRegEx)) {
				// We want to convert it back to the dateRawFormat
				const momentDate = moment(date, this.translation.dateFormat);

				const pastBlocked = this.blockedBefore;
				const enteredDate = this.$i18n.locale === 'fr'
					// eslint-disable-next-line no-underscore-dangle
					? this.frenchDateToUniversal(momentDate._i)
					// eslint-disable-next-line no-underscore-dangle
					: this.englishDateToUniversal(momentDate._i);

				if (momentDate.isValid()) {
					// Change the format for the one the datepicker library understands
					const formattedDate = momentDate.format(this.dateRawFormat);

					if (input === 'start') {
						// prevent blocked dates from being validated
						if (pastBlocked && (enteredDate < this.minDate)) {
							if (formattedDate < this.minDate) {
								this.startHint = null;
								this.startErrors = [this.messages[this.$i18n.locale].startDateError];
								this.$emit('errorLogged');
							}
							return;
						}

						if ((pastBlocked && (enteredDate >= this.minDate)) || !pastBlocked) {
							if (formattedDate >= this.minDate) {
								this.startHint = this.translation.dateFormat;
								this.startErrors = [];
							}
							this.updateStartDate(formattedDate);
							this.focusOnInput('end');

							const monthEntered = enteredDate.split('-')[1];
							if (this.minDate) {
								const currentMonth = this.minDate.split('-')[1];

								if (monthEntered > currentMonth) {
									this.activatePreviousMonthButton();
								}
							}

							return;
						}
					}

					if (input === 'end') {
						if (!this.dateStart) {
							this.endHint = null;
							this.endErrors = [this.messages[this.$i18n.locale].startDateMissing];
							this.$emit('errorLogged');
							return;
						}

						if (formattedDate < this.dateStart) {
							this.endHint = null;
							this.endErrors = [this.messages[this.$i18n.locale].endDateTooEarly];
							this.$emit('errorLogged');
						} else {
							this.endHint = this.translation.dateFormatGuide;
							this.endErrors = [];
						}

						this.updateEndDate(formattedDate);
					}
				}
			}
		},

		updateStartDate(value = '') {
			this.dateStart = value;
			this.$emit('startDateChanged', value);
			this.addClearButton();

			if (this.type === 'range') {
				this.inputCurrentlyOpened = 'end';
			}
		},

		updateEndDate(value = '') {
			this.dateEnd = value;
			this.$emit('endDateChanged', value);
			this.addClearButton();

			if (this.type === 'range') {
				this.inputCurrentlyOpened = null;
			}
		},

		// ] Methods that manage clearing of data

		addClearButton() {
			if (!this.$refs['airbnb-datepicker']) {
				return;
			}

			const datepicker = this.$refs['airbnb-datepicker'].$el;
			const buttons = datepicker.querySelector('.asd__action-buttons');
			const clear = datepicker.querySelector('.datepicker__clear');

			if (this.clearable) {
				if (!clear) {
					const button = document.createElement('button');
					button.classList.add('datepicker__clear');
					button.setAttribute('type', 'button');
					button.innerHTML = this.messages[this.$i18n.locale].clear;
					button.addEventListener('click', this.clear, false);
					this.hasClearButton = true;

					if (buttons) {
						buttons.prepend(button);
					} else {
						const actionButtons = document.createElement('div');
						actionButtons.classList.add('asd__action-buttons');
						datepicker.append(actionButtons);
						actionButtons.append(button);
					}
				} else {
					clear.innerHTML = this.messages[this.$i18n.locale].clear;
				}
			}
		},

		clear() {
			this.validateAndSetDateStart();
			this.validateAndSetDateEnd();
		},

		// ] Methods that manage blur

		blurredInline(input, date) {
			this.blurredValidation(input, date);
			if (!this.isPopup && this.inputCurrentlyOpened === input) {
				this.inputCurrentlyOpened = null;
			}
		},

		blurredValidation(input, date) {
			const currentDateStart = this.formatDate(this.dateStart);
			const currentDateEnd = this.formatDate(this.dateEnd);

			// If the given date is the same as in the component data
			// it means the changes came from the Datepicker library
			// and we don't want to do anything
			if (
				(input === 'start' && date === currentDateStart)
				|| (input === 'end' && date === currentDateEnd)
			) {
				return;
			}

			this.$refs[input].value = '';
		},

		// ] Methods that manage focus

		focusOnInput(input) {
			setTimeout(() => {
				if (this.$refs[input]) {
					const componentElement = this.$refs[input];
					const inputElement = componentElement.$el.querySelector('.field__input');

					if (inputElement) {
						inputElement.focus();
					}
				}
			}, 100);
		},

		isInputFocus(input) {
			const componentElement = this.$refs[input];

			if (componentElement) {
				const inputElement = componentElement.$el.querySelector('.field__input');
				return inputElement && inputElement === document.activeElement;
			}

			return false;
		},

		// ] Methods that manage Calendar visibility/position

		calculateOffset() {
			setTimeout(() => {
				if (this.position === 'top' && this.layout === 'popup' && this.$refs['airbnb-datepicker']) {
					const popup = this.$refs['airbnb-datepicker'].$el;

					if (popup.clientHeight > 0) {
						this.popupHeight = popup.clientHeight;
					}
				}
			}, 50);
		},

		closeDatepicker() {
			this.$parent.$emit('datePickerToggle', false);

			this.$nextTick(() => {
				this.inputCurrentlyOpened = null;
				this.triggered = false;
				this.renderCalendar = false;

				// If the datepicker was close while we clicked on the input
				setTimeout(() => {
					if (this.isInputFocus('start')) {
						this.inputCurrentlyOpened = 'start';
						this.triggered = true;
						this.focusOnInput('start');
					} else if (this.isInputFocus('end')) {
						this.inputCurrentlyOpened = 'end';
						this.triggered = true;
						this.focusOnInput('end');
					}
				}, 100);
			});
		},

		// ] Methods that manage inactive calendar dates

		deactivatePreviousMonthButton() {
			if (this.blockedBefore && this.$refs.datepicker) {
				// select correct datepicker
				const timeLimitedDatepicker = this.$refs.datepicker;
				// add class name to datepicker so correct CSS is applied
				timeLimitedDatepicker.classList.add('datepicker__container--prev-button-blocked');
				// select correct button div
				const buttonDiv = timeLimitedDatepicker.getElementsByClassName('asd__change-month-button--previous')[0];

				if (buttonDiv) {
					// select correct button
					const button = buttonDiv.getElementsByTagName('button')[0];

					// deactivate button
					button.disabled = true;
				}
			}
		},

		activatePreviousMonthButton() {
			// select correct datepicker
			const timeLimitedDatepicker = this.$refs.datepicker;
			// add class name to datepicker so correct CSS is applied
			timeLimitedDatepicker.classList.remove('datepicker__container--prev-button-blocked');
			// select correct button div
			const buttonDiv = timeLimitedDatepicker.getElementsByClassName('asd__change-month-button--previous')[0];
			// select correct button
			const button = buttonDiv.getElementsByTagName('button')[0];

			// deactivate button
			button.disabled = false;
		},

		onCurrentMonth(value) {
			// Get the number of the month for the leftmost calendar in the datepicker
			const firstCalendarMonth = parseInt(value[0].split('-')[1], 10);

			// If the first calendar month is the current month, deactivate the button
			if (firstCalendarMonth === this.currentMonth) {
				this.deactivatePreviousMonthButton();
			}
		},

		onFutureMonth() {
			this.activatePreviousMonthButton();
		},

		// ] Methods that manage inactive calendar

		setCalendarButtons() {
			if (this.$refs.datepicker) {
				// select correct datepicker
				const { datepicker } = this.$refs;

				// select month button divs
				const buttonDivPrevious = datepicker.getElementsByClassName('asd__change-month-button--previous')[0];
				const buttonDivNext = datepicker.getElementsByClassName('asd__change-month-button--next')[0];

				if (buttonDivPrevious && buttonDivNext) {
					// select correct button
					const buttonPrevious = buttonDivPrevious.getElementsByTagName('button')[0];
					const buttonNext = buttonDivNext.getElementsByTagName('button')[0];

					// deactivate button
					buttonPrevious.disabled = this.disabled;
					buttonNext.disabled = this.disabled;
				}

				// select & disable  date buttons
				const dates = datepicker.getElementsByClassName('asd__day-button');
				const allDates = [...dates];
				allDates.forEach((button) => { button.disabled = this.disabled; });
			}
		},
	},
};
</script>

<style lang="scss" src="./datepicker.scss" />
